import { MenuItem, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, OutlinedInput, Select, TextField, Box, ListItemText, ToggleButtonGroup, ToggleButton, FormHelperText } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import BasicDatePicker from "../../../../../../components/BasicDatePicker";
import moment, { Moment } from "moment";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import SelecaoSazonalidade from "./SelecaoSazonalidade";
import { coresSazonalidade } from "./utils";
import { TOpenModalSazonalidade } from "..";

export default function DialogCriacaoSazonalidade({
  open,
  handleClose,
  defaultValue,
}: DialogCriacaoSazonalidadeProps) {
  const queryClient = useQueryClient();

  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])

  //Querys e mutations
  const { mutate: criarSazonalidade, isLoading: isLoadingCriarSazonalidade } =
    useMutation({
      mutationKey: "cadastrar-sazonalidade",
      mutationFn: (inputs: TSazonalidades) =>
        axiosClient.post(
          "/estoque/configuracoes/cadastrar-sazonalidade",
          inputs
        ),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao cadastrar sazonalidade!", {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["pesquisar-sazonalidade"]);
        enqueueSnackbar("Sazonalidade cadastrada com sucesso!", {
          variant: "success",
        });
        handleClose();
      },
    });

  const { mutate: attSazonalidade, isLoading: isLoadingAttSazonalidade } =
    useMutation({
      mutationKey: "atualizar-sazonalidade",
      mutationFn: (inputs: TSazonalidades) =>
        axiosClient.put(
          `/estoque/configuracoes/atualizar-sazonalidade/${valoresIniciais?.id}`,
          inputs
        ),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao atualizar sazonalidade!", {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["detalhar-sazonalidade"]);
        queryClient.invalidateQueries(["pesquisar-sazonalidade"]);
        enqueueSnackbar("Sazonalidade atualizada com sucesso!", {
          variant: "success",
        });
        handleClose();
      },
    });

  const { data: dataProdutosSazionalidadeRequest, isFetching: isLoadingDetalhes } = useQuery({
    queryKey: ["detalhar-sazonalidade", valoresIniciais?.id],
    queryFn: () =>
      axiosClient.get(
        `/estoque/configuracoes/detalhar-sazonalidade/${valoresIniciais?.id}`,
        {}
      ),
    keepPreviousData: true,
    enabled: isEdit,
  });
  //

  const dataProdutosSazionais: TRequestSazionalidade = useMemo(() => {
    const produtosRequest =
      dataProdutosSazionalidadeRequest?.data?.produtos || [];
    const produtos = produtosRequest.map((produto: any) => ({
      codigo: produto.id,
      nome: produto.nome,
    }));
    const sazonalidade =
      dataProdutosSazionalidadeRequest?.data?.sazonalidade || {};
    return {
      produtos: produtos,
      sazonalidade: sazonalidade,
    };
  }, [dataProdutosSazionalidadeRequest]);

  const defaultForm = {
    produtos: [],
    cor: "",
    nome: "",
    sazonalidade_inicio: moment().subtract(1, "day").format("YYYY-MM-DD"),
    sazonalidade_fim: moment().add("3", "months").format("YYYY-MM-DD"),
    periodo: "anual" as TPeriodoProps,
  };
  const [form, setForm] = useState<TSazonalidades>(defaultForm);
  const podeContinuar = useMemo(() => form?.nome === "" || form?.cor === "",[form?.nome, form?.cor]);

  useEffect(() => {
    if (dataProdutosSazionais) {
      if (isEdit) {
        const defaultForm = {
          produtos:
            isEdit && dataProdutosSazionais?.produtos
              ? dataProdutosSazionais?.produtos
              : [],
          cor:
            isEdit && dataProdutosSazionais?.sazonalidade
              ? dataProdutosSazionais?.sazonalidade.cores
              : "",
          nome:
            isEdit && dataProdutosSazionais?.sazonalidade
              ? dataProdutosSazionais?.sazonalidade.nome
              : "",
          sazonalidade_inicio:
            isEdit && dataProdutosSazionais?.sazonalidade
              ? moment(
                dataProdutosSazionais.sazonalidade.inicio,
                "DD/MM/YY"
              ).format("YYYY-MM-DD")
              : moment().subtract(1, "day").format("YYYY-MM-DD"),
          sazonalidade_fim:
            isEdit && dataProdutosSazionais?.sazonalidade
              ? moment(dataProdutosSazionais.sazonalidade.fim, "DD/MM/YY")
                .subtract(0, "year")
                .format("YYYY-MM-DD")
              : moment().add("3", "months").format("YYYY-MM-DD"),
          periodo:
            isEdit && dataProdutosSazionais?.sazonalidade
              ? dataProdutosSazionais?.sazonalidade.periodo
              : ("anual" as TPeriodoProps),
        };
        setForm(defaultForm);
      }
    }
  }, [dataProdutosSazionais, isEdit]);

  //Funções manipuladoras
  const handleChangeForm = useCallback(
    (prop: "nome" | "cor" | "periodo" | "sazonalidade_inicio" | "sazonalidade_fim", value: string) => {
      setForm((prevState) => ({ ...prevState, [prop]: value }));
    }, []);

  const handleChangeProdutos = (codigo: number, nome: string, checkbox: boolean) => {
    let newSelecionados: Array<InputProdutoSazonalidade> = [...form.produtos];
    const params = {
      codigo,
      nome,
    };

    if (!checkbox) {
      newSelecionados = newSelecionados.filter((i) => i.codigo !== codigo);
    }

    if (checkbox) {
      let index = newSelecionados.findIndex((i) => i.codigo === codigo);
      if (index < 0) {
        newSelecionados.push(params);
      } else {
        newSelecionados[index] = params;
      }
    }
    setForm((prevState) => ({ ...prevState, produtos: newSelecionados }));
  };

  const handleSubmitValues = useCallback(() => {
    const params = {
      produtos: form.produtos,
      nome: form.nome,
      sazonalidade_inicio: form.sazonalidade_inicio,
      sazonalidade_fim: form.sazonalidade_fim,
      cor: form.cor,
      periodo: form.periodo,
    };
    if (isEdit) {
      attSazonalidade(params);
    }
    if (!isEdit) {
      criarSazonalidade(params);
    }
  }, [criarSazonalidade, attSazonalidade, isEdit, form]);

  const onVincular = useCallback((produtos: InputProdutoSazonalidade[]) => {
      setForm((prevState) => {
        const novosProdutos = produtos.filter(
          (novoProduto) =>
            !prevState.produtos.some(
              (produto) => produto.codigo === novoProduto.codigo
            )
        );
        return {
          ...prevState,
          produtos: [...prevState.produtos, ...novosProdutos],
        };
      });
    },
    [form.produtos]
  );
  //

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {`${!isEdit ? "Adicionar" : "Editar"} sazonalidade`}
      </DialogTitle>

      {isLoadingDetalhes && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}
      <DialogContent>
        <Grid container spacing={1}>
          <Grid size={{xs: 12}}>
            <TextField
              fullWidth
              id="outlined-basic"
              label="Nome"
              variant="outlined"
              margin="normal"
              value={form.nome}
              onChange={(e) => handleChangeForm("nome", e.target.value)}
            />
          </Grid>
          <Grid size={{xs: 12}}>
            <Grid container spacing={2}>
              <Grid size={{xs: 12}}>
                <FormControl component="fieldset">
                  <ToggleButtonGroup
                    size="small"
                    color="primary"
                    value={form.periodo}
                    exclusive
                    onChange={(event, value) =>
                      handleChangeForm("periodo", value)
                    }
                    aria-label="Platform"
                  >
                    <ToggleButton value="anual">Por ano</ToggleButton>
                    <ToggleButton value="periodo">Por período</ToggleButton>
                  </ToggleButtonGroup>
                  {form.periodo === "periodo" && (
                    <FormHelperText sx={{ mt: 1, marginX: 0 }}>
                      <b>"Por período"</b> para eventos que ocorrem uma única
                      vez em um período específico.
                    </FormHelperText>
                  )}
                  {form.periodo === "anual" && (
                    <FormHelperText sx={{ mt: 1, marginX: 0 }}>
                      <b>"Por ano"</b> para eventos que se repetem todos os anos
                      no mesmo período.
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{xs: 6}}>
                <BasicDatePicker
                  label="Início"
                  formatString={
                    form.periodo === "anual" ? "DD/MM" : "DD/MM/YYYY"
                  }
                  value={moment(form.sazonalidade_inicio)}
                  onChange={(data) =>
                    handleChangeForm(
                      "sazonalidade_inicio",
                      (data as Moment).format("YYYY-MM-DD")
                    )
                  }
                />
              </Grid>
              <Grid size={{xs: 6}}>
                <BasicDatePicker
                  label="Fim"
                  formatString={
                    form.periodo === "anual" ? "DD/MM" : "DD/MM/YYYY"
                  }
                  value={moment(form.sazonalidade_fim)}
                  onChange={(data) =>
                    handleChangeForm(
                      "sazonalidade_fim",
                      (data as Moment).format("YYYY-MM-DD")
                    )
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{xs: 12}}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="info_extras">Cor</InputLabel>
              <Select
                labelId="info_extras"
                value={form.cor}
                input={<OutlinedInput label="Cor" />}
                onChange={(e) => handleChangeForm("cor", e.target.value)}
              >
                {coresSazonalidade.map((cor) => {
                  return (
                    <MenuItem key={cor.hash} value={cor.hash}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 50,
                            backgroundColor: cor.hash,
                            marginRight: 2,
                            border: "1px solid #000",
                          }}
                        />
                        <ListItemText primary={cor.nome} />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{xs: 12}}>
            <SelecaoSazonalidade
              produtos={form.produtos}
              onVincular={(produtos: InputProdutoSazonalidade[]) =>
                onVincular(produtos)
              }
              onChangeCheckbox={(
                codigo: number,
                nome: string,
                valor: boolean
              ) => handleChangeProdutos(codigo, nome, valor)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          autoFocus
          disabled={
            isLoadingCriarSazonalidade ||
            isLoadingAttSazonalidade ||
            podeContinuar
          }
          onClick={handleSubmitValues}
        >
          {(isLoadingCriarSazonalidade || isLoadingAttSazonalidade) && (
            <CircularProgress size={20} sx={{ marginRight: 1 }} />
          )}
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DialogCriacaoSazonalidadeProps {
  open: boolean;
  handleClose: () => void;
  defaultValue?: TOpenModalSazonalidade
}
type TSazonalidades = {
  cor: string;
  nome: string;
  sazonalidade_inicio: string;
  sazonalidade_fim: string;
  produtos: Array<InputProdutoSazonalidade>;
  periodo: TPeriodoProps;
};

export type InputProdutoSazonalidade = {
  codigo: number;
  nome: string;
};
type TRequestSazionalidade = {
  produtos: Array<InputProdutoSazonal>;
  sazonalidade: {
    id: number;
    cores: string;
    nome: string;
    inicio: string;
    fim: string;
    periodo: TPeriodoProps;
  };
};

type InputProdutoSazonal = {
  codigo: number;
  nome: string;
};
export type TPeriodoProps = "anual" | "periodo";
